// Utility per la gestione dei token JWT

export interface JWTPayload {
  sub: string;
  exp: number;
  iat?: number;
}

/**
 * Decodifica un token JWT senza verificare la firma
 * @param token Token JWT
 * @returns Payload decodificato o null se invalido
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Verifica se un token è scaduto
 * @param token Token JWT
 * @returns true se il token è scaduto
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
}

/**
 * Verifica se un token scadrà tra X secondi
 * @param token Token JWT
 * @param secondsBeforeExpiry Secondi prima della scadenza
 * @returns true se il token scadrà tra i secondi specificati
 */
export function isTokenExpiringWithin(token: string, secondsBeforeExpiry: number = 600): boolean {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  return (decoded.exp - currentTime) <= secondsBeforeExpiry;
}

/**
 * Ottiene il tempo rimanente prima della scadenza del token in secondi
 * @param token Token JWT
 * @returns Secondi rimanenti prima della scadenza, 0 se scaduto
 */
export function getTokenTimeRemaining(token: string): number {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return 0;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  const remaining = decoded.exp - currentTime;
  return Math.max(0, remaining);
}